import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import "./Header.css";
import "../../assets/css/Responsive/Header_Responsive.css";
import { fetchGlobalSettings } from "../../api/globalsetting";

import hamburger from "../../assets/images/Header/hamburger.svg";
import closeicon from "../../assets/images/Header/hamburger_close.svg";
import rightarrow from "../../assets/images/Header/Arrow Right Up.svg";

const sanitizeString = (value) => {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    return trimmed.length ? trimmed : "";
};

const sanitizeImage = (image) => {
    const url = sanitizeString(image?.url || "");
    if (!url) return null;
    return { url, alt: sanitizeString(image?.alt || "") };
};

const sanitizeNavLinks = (links) => {
    if (!Array.isArray(links)) return [];
    return links
        .map((item) => {
            const text = sanitizeString(item?.text || "");
            const url = sanitizeString(item?.url || "");
            const showMobileOnly = Boolean(item?.show_mobile_only);
            if (!text || !url) return null;
            return { text, url, showMobileOnly };
        })
        .filter(Boolean);
};

// Helper to check if URL is internal
const isInternalUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim();
    if (trimmed.startsWith("/")) return true;
    if (trimmed.startsWith("mailto:") || trimmed.startsWith("tel:")) return false;
    try {
        const urlObj = new URL(trimmed, window.location.origin);
        return urlObj.origin === window.location.origin;
    } catch {
        return false;
    }
};

// Helper function to parse HTML and extract data attributes using regex
const parseXolaCheckoutHtml = (htmlString) => {
    if (!htmlString || typeof htmlString !== "string") return null;

    try {
        // Extract data attributes using regex
        const sellerMatch = htmlString.match(/data-seller=["']([^"']+)["']/);
        const versionMatch = htmlString.match(/data-version=["']([^"']+)["']/);
        const experienceMatch = htmlString.match(/data-experience=["']([^"']+)["']/);

        const seller = sellerMatch ? sellerMatch[1] : '';
        const version = versionMatch ? versionMatch[1] : '';
        const experience = experienceMatch ? experienceMatch[1] : '';

        // Only return if we have at least seller or experience (required for Xola)
        if (!seller && !experience) return null;

        return {
            seller,
            version,
            experience,
        };
    } catch (error) {
        return null;
    }
};

const sanitizeHeaderData = (apiData) => {
    if (!apiData) return null;

    const toplineText = sanitizeString(apiData.topline?.text || "");
    const logo = sanitizeImage(apiData.header?.logo);
    const navLinks = sanitizeNavLinks(apiData.header?.nav_links);
    const buyButtonText = sanitizeString(apiData.header?.buy_button_text || "");
    const buyButtonUrl = sanitizeString(apiData.header?.buy_button_url || "");

    // Extract Xola checkout HTML from buy_button_url (it contains the HTML div)
    // Also check other possible fields as fallback
    const xolaCheckoutHtml = buyButtonUrl ||
        sanitizeString(apiData.header?.buy_button_html || apiData.header?.xola_checkout_html || "");
    const xolaAttributes = xolaCheckoutHtml ? parseXolaCheckoutHtml(xolaCheckoutHtml) : null;

    // Debug: Log if we have HTML but couldn't parse attributes
    if (xolaCheckoutHtml && !xolaAttributes) {
    }

    return {
        toplineText,
        header: {
            logo,
            navLinks,
            buyButton: buyButtonText ? {
                text: buyButtonText,
                url: buyButtonUrl,
                xolaAttributes: xolaAttributes
            } : null,
        },
    };
};

const hasRenderableContent = (payload) => {
    if (!payload) return false;
    const { toplineText, header } = payload;
    return Boolean(toplineText || header.logo || header.navLinks.length || header.buyButton);
};

function Header() {
    const [data, setData] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [showStickyNav, setShowStickyNav] = useState(false);
    const headerRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        fetchGlobalSettings()
            .then((apiData) => {
                const sanitized = sanitizeHeaderData(apiData);
                setData(hasRenderableContent(sanitized) ? sanitized : null);
            })
            .catch((err) => { });
    }, []);

    // ============================================
    // XOLA INTEGRATION - Simple implementation per official docs
    // Step 1: Load the Xola checkout.js script
    // Step 2: Xola automatically finds divs with "xola-checkout" class and makes them work
    // No custom click handlers needed!
    // ============================================
    useEffect(() => {
        // Only load script if we have Xola attributes
        if (!data || !data.header?.buyButton?.xolaAttributes) return;

        const { seller, experience } = data.header.buyButton.xolaAttributes;
        if (!seller || !experience) return;

        // Check if script is already loaded
        const existingScript = document.querySelector('script[src="https://xola.com/checkout.js"]');
        if (existingScript) return;

        // Load Xola script exactly as per documentation
        (function () {
            var co = document.createElement("script");
            co.type = "text/javascript";
            co.async = true;
            co.src = "https://xola.com/checkout.js";
            var s = document.getElementsByTagName("script")[0];
            s.parentNode.insertBefore(co, s);
        })();
    }, [data]);

    // No custom click handlers needed - Xola handles everything!
    // The button is just a simple div with data attributes.
    // Xola's script will automatically make it work.

    // Global function for hero section to trigger Xola checkout
    useEffect(() => {
        window.openXolaCheckout = () => {
            // Simply find and click the Xola div button
            const buyButton = document.querySelector('.buy, .mobile-buy');
            if (buyButton) {
                buyButton.click();
                return true;
            }
            return false;
        };

        return () => {
            delete window.openXolaCheckout;
        };
    }, []);

    // Handle clicks on /#buynow-popup links to trigger Xola iframe
    useEffect(() => {
        const handleBuyNowPopupClick = (e) => {
            const target = e.target.closest('a[href*="#buynow-popup"]');
            if (target) {
                e.preventDefault();
                e.stopPropagation();

                // Find and click the Xola buy button
                const xolaButton = document.querySelector('.buy, .mobile-buy');
                if (xolaButton) {
                    xolaButton.click();
                }
            }
        };

        // Add click listener to document to catch all clicks
        document.addEventListener('click', handleBuyNowPopupClick, true);

        return () => {
            document.removeEventListener('click', handleBuyNowPopupClick, true);
        };
    }, []);




    // Simple scroll-based approach: add sticky class when scrolled past header + topline
    useEffect(() => {
        if (!data) return;

        const { toplineText } = data;
        const toplineHeight = toplineText ? 36 : 0; // Topline height

        const handleScroll = () => {
            // Recalculate header height on each scroll (it might change)
            const headerHeight = headerRef.current ? headerRef.current.offsetHeight : 80; // Header height (default 80px)
            const scrollThreshold = toplineHeight + headerHeight; // Total height to scroll past (36 + 81 = 117)
            const scrollY = window.scrollY || window.pageYOffset;

            // Logic: 
            // - When scrollY > scrollThreshold (117px): Add sticky (header should stick with us as we scroll)
            // - When scrollY <= 0px: Remove sticky (only at the very top, exactly 0px)
            // - Between 0px and 117px: Keep sticky visible (smooth transition, no gap where header disappears)
            // This ensures the sticky header stays visible during transition and only disappears at exactly 0px

            if (scrollY <= 0) {
                // At exactly 0px - remove sticky (back to normal header, smooth transition)
                setShowStickyNav(false);
            } else if (scrollY > scrollThreshold) {
                // Past threshold - add sticky (header sticks with us as we scroll)
                setShowStickyNav(true);
            }
            // Between 0px and scrollThreshold (117px): Keep current state (sticky stays visible)
        };

        // Use requestAnimationFrame for smooth scroll handling
        let rafId = null;
        const onScroll = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                handleScroll();
                rafId = null;
            });
        };

        window.addEventListener('scroll', onScroll, { passive: true });

        // Check initial scroll position
        handleScroll();

        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [data]);

    // Close mobile menu when screen is resized to desktop size (above 992px)
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 992 && mobileOpen) {
                setMobileOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);

        // Check on mount in case screen is already desktop size
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [mobileOpen]);

    useEffect(() => {
        if (!mobileOpen) return;

        const body = document.body;
        const html = document.documentElement;
        const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;

        // Save scroll position
        body.dataset.scrollY = scrollY;

        // Get mobile nav element for checking if scroll should be allowed
        const mobileNav = document.getElementById("mobileNav");

        // Lock body - more aggressive approach
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.left = "0";
        body.style.right = "0";
        body.style.width = "100%";
        body.style.overflow = "hidden";
        body.style.height = "100vh";
        // Don't use touchAction: "none" as it blocks clicks - use "pan-y" to only prevent vertical panning
        body.style.touchAction = "pan-y pinch-zoom";

        // Lock html element for better compatibility
        html.style.overflow = "hidden";
        html.style.height = "100%";
        html.style.position = "fixed";
        html.style.width = "100%";
        html.style.touchAction = "pan-y pinch-zoom";

        // Helper to check if target is a clickable element (button, link, etc.)
        const isClickable = (target) => {
            if (!target) return false;
            const tagName = target.tagName?.toLowerCase();
            if (['button', 'a', 'input', 'select', 'textarea'].includes(tagName)) {
                return true;
            }
            // Check if it's inside a clickable element
            const clickableParent = target.closest('button, a, [onclick], [role="button"]');
            return clickableParent !== null;
        };

        // Prevent ALL scrolling events - more comprehensive
        const preventScroll = (e) => {
            // Always allow clicks on buttons, links, and interactive elements
            if (isClickable(e.target)) {
                return; // Allow clicks to work normally
            }

            // Allow scrolling within the mobile menu only
            if (mobileNav && mobileNav.contains(e.target)) {
                // Check if the target is actually scrollable within the menu
                const scrollableElement = e.target.closest('.mobile-menu-main, .mobile-menu');
                if (scrollableElement) {
                    const isScrollable = scrollableElement.scrollHeight > scrollableElement.clientHeight;
                    if (isScrollable) {
                        return; // Allow scrolling within menu if it's scrollable
                    }
                }
            }
            // Prevent ALL other scrolling
            e.preventDefault();
            e.stopPropagation();
            return false;
        };

        // Prevent wheel scrolling (mouse wheel)
        const preventWheel = (e) => {
            // Allow clicks on buttons and links
            if (isClickable(e.target)) {
                return;
            }

            if (mobileNav && mobileNav.contains(e.target)) {
                const scrollableElement = e.target.closest('.mobile-menu-main, .mobile-menu');
                if (scrollableElement) {
                    const isScrollable = scrollableElement.scrollHeight > scrollableElement.clientHeight;
                    if (isScrollable) {
                        return; // Allow scrolling within menu
                    }
                }
            }
            e.preventDefault();
            e.stopPropagation();
            return false;
        };

        // Prevent scroll event on window
        const preventWindowScroll = (e) => {
            e.preventDefault();
            window.scrollTo(0, scrollY);
            return false;
        };

        // Add event listeners with passive: false to allow preventDefault
        // Use capture phase to catch events early, but don't block touchstart (needed for clicks)
        document.addEventListener("touchmove", preventScroll, { passive: false, capture: true });
        document.addEventListener("wheel", preventWheel, { passive: false, capture: true });
        // Don't prevent touchstart - it's needed for clicks to work
        document.addEventListener("scroll", preventWindowScroll, { passive: false, capture: true });
        window.addEventListener("scroll", preventWindowScroll, { passive: false, capture: true });
        window.addEventListener("wheel", preventWheel, { passive: false, capture: true });
        window.addEventListener("touchmove", preventScroll, { passive: false, capture: true });

        // Cleanup function
        return () => {
            // Remove all event listeners
            document.removeEventListener("touchmove", preventScroll, { capture: true });
            document.removeEventListener("wheel", preventWheel, { capture: true });
            document.removeEventListener("scroll", preventWindowScroll, { capture: true });
            window.removeEventListener("scroll", preventWindowScroll, { capture: true });
            window.removeEventListener("wheel", preventWheel, { capture: true });
            window.removeEventListener("touchmove", preventScroll, { capture: true });

            // Restore body styles
            body.style.position = "";
            body.style.top = "";
            body.style.left = "";
            body.style.right = "";
            body.style.width = "";
            body.style.overflow = "";
            body.style.height = "";
            body.style.touchAction = "";

            // Restore html styles
            html.style.overflow = "";
            html.style.height = "";
            html.style.position = "";
            html.style.width = "";
            html.style.touchAction = "";

            // Restore scroll position
            const savedScrollY = body.dataset.scrollY || 0;
            window.scrollTo(0, Number(savedScrollY));
            delete body.dataset.scrollY;
        };
    }, [mobileOpen]);


    if (!data) return null;

    const { toplineText, header } = data;
    const hasLogo = Boolean(header.logo);
    const hasNavLinks = header.navLinks.length > 0;
    const hasBuyButton = Boolean(header.buyButton);

    const openMenu = () => setMobileOpen(true);
    const closeMenu = () => setMobileOpen(false);

    return (
        <>
            <div className={`header-wrapper ${showStickyNav ? 'is-sticky' : ''}`}>
                {toplineText && (
                    <div className="topline">
                        <div className="canvas">
                            <p>{toplineText}</p>
                        </div>
                    </div>
                )}

                <header className="header" ref={headerRef}>
                    <div className="canvas">
                        <div className="header-inner">
                            <div className="navwrap">
                                {hasLogo && (
                                    <Link className="brand" to="/">
                                        <img src={header.logo.url} alt={header.logo.alt || "Logo"} />
                                    </Link>
                                )}

                                <button className="menu-toggle" onClick={openMenu} aria-label="Open Menu">
                                    <img src={hamburger} alt="Menu" />
                                </button>

                                {hasNavLinks && (
                                    <nav className="nav_links">
                                        {header.navLinks
                                            .filter((item) => !item.showMobileOnly) // Only show links that are NOT mobile-only
                                            .map((item, index) => {
                                                const isInternal = isInternalUrl(item.url);
                                                const isActive = isInternal && location.pathname === item.url;

                                                const handleClick = (e) => {
                                                    // Allow browser to handle: ctrl+click, cmd+click, shift+click (open in new tab/window)
                                                    if (e.ctrlKey || e.metaKey || e.shiftKey) {
                                                        return; // Let browser handle it naturally
                                                    }
                                                    // Regular left click - use React Router
                                                    e.preventDefault();
                                                    navigate(item.url);
                                                };

                                                return isInternal ? (
                                                    <a
                                                        key={`${item.text}-${index}`}
                                                        href={item.url}
                                                        onClick={handleClick}
                                                        className={isActive ? 'active' : ''}
                                                    >
                                                        {item.text}
                                                    </a>
                                                ) : (
                                                    <a
                                                        key={`${item.text}-${index}`}
                                                        href={item.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {item.text}
                                                    </a>
                                                );
                                            })}
                                    </nav>
                                )}

                                {hasBuyButton && (
                                    header.buyButton.xolaAttributes ? (
                                        <div
                                            className="xola-custom xola-checkout"
                                            data-seller={header.buyButton.xolaAttributes.seller}
                                            data-version={header.buyButton.xolaAttributes.version}
                                            data-experience={header.buyButton.xolaAttributes.experience}
                                        >
                                            <button
                                                className="buy"
                                                type="button"
                                            >
                                                <span className="text">{header.buyButton.text}</span>
                                                <span className="btn-img">
                                                    <img src={rightarrow} alt="right arrow" />
                                                </span>
                                            </button>
                                        </div>
                                    ) : (
                                        <button className="buy" type="button">
                                            <span className="text">{header.buyButton.text}</span>
                                            <span className="btn-img">
                                                <img src={rightarrow} alt="right arrow" />
                                            </span>
                                        </button>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </header>
            </div>

            {/* Mobile Nav - Moved outside header to escape stacking context */}
            {(toplineText || hasLogo || hasNavLinks || hasBuyButton) && (
                <div className={`mobile-nav ${mobileOpen ? "active" : ""}`} id="mobileNav">
                    {toplineText && <p className="mobile-hours">{toplineText}</p>}

                    <div className="mobile-top">
                        {hasLogo && (
                            <Link
                                className="brand"
                                to="/"
                                onClick={(e) => {
                                    // Allow browser to handle: ctrl+click, cmd+click, shift+click
                                    if (e.ctrlKey || e.metaKey || e.shiftKey) {
                                        return;
                                    }
                                    // Regular left click - use React Router
                                    e.preventDefault();

                                    // Close menu first, then navigate
                                    closeMenu();
                                    // Small delay to let menu start closing before page transition
                                    setTimeout(() => {
                                        navigate("/");
                                    }, 50);
                                }}
                            >
                                <img
                                    src={header.logo.url}
                                    alt={header.logo.alt || "Mobile Logo"}
                                    className="mobile-logo"
                                />
                            </Link>
                        )}

                        <button className="close-btn" onClick={closeMenu} aria-label="Close Menu">
                            <img src={closeicon} alt="Close" />
                        </button>
                    </div>

                    {(hasNavLinks || hasBuyButton) && (
                        <div className="mobile-menu-main">
                            {hasNavLinks && (
                                <ul className="mobile-menu">
                                    {header.navLinks.map((item, index) => {
                                        const isInternal = isInternalUrl(item.url);
                                        const isActive = isInternal && location.pathname === item.url;

                                        const handleClick = (e) => {
                                            // Allow browser to handle: ctrl+click, cmd+click, shift+click (open in new tab/window)
                                            if (e.ctrlKey || e.metaKey || e.shiftKey) {
                                                return; // Let browser handle it naturally
                                            }
                                            // Regular left click - use React Router
                                            e.preventDefault();

                                            // Close menu first, then navigate
                                            closeMenu();
                                            // Small delay to let menu start closing before page transition
                                            setTimeout(() => {
                                                navigate(item.url);
                                            }, 50);
                                        };

                                        return (
                                            <li key={`${item.text}-${index}`} className="dropdown">
                                                {isInternal ? (
                                                    <a
                                                        href={item.url}
                                                        onClick={handleClick}
                                                        className={isActive ? 'active' : ''}
                                                    >
                                                        {item.text}
                                                    </a>
                                                ) : (
                                                    <a
                                                        href={item.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {item.text}
                                                    </a>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            {hasBuyButton && (
                                header.buyButton.xolaAttributes ? (
                                    <div
                                        className="xola-custom xola-checkout"
                                        data-seller={header.buyButton.xolaAttributes.seller}
                                        data-version={header.buyButton.xolaAttributes.version}
                                        data-experience={header.buyButton.xolaAttributes.experience}
                                    >
                                        <button
                                            className="buy mobile-buy"
                                            type="button"
                                        >
                                            <span className="text">{header.buyButton.text}</span>
                                            <span className="btn-img">
                                                <img src={rightarrow} alt="right arrow" />
                                            </span>
                                        </button>
                                    </div>
                                ) : (
                                    <button className="buy mobile-buy" type="button">
                                        <span className="text">{header.buyButton.text}</span>
                                        <span className="btn-img">
                                            <img src={rightarrow} alt="right arrow" />
                                        </span>
                                    </button>
                                )
                            )}
                        </div>
                    )}
                </div>
            )}

        </>
    );
}

export default Header;
